import { useMemo, useState } from "react";
import { Localization } from "@viamap/viamap2-common";
import { GlassButton } from "src/components/GlassButtons";
import { normalizeDatoLoebenrKey } from "src/modules/ANNModule/ANNServitutProvenance";
import { getRowDatoLoebenr, getRowHeading } from "./ServitutRedegoerelseRow";

type FolderMatchRow = {
   key: string;
   label: string;
   heading: string;
   inMapit: boolean;
   inFolder: boolean;
};

function parseDatoLoebenrFromFileName(fileName: string): string {
   const base = fileName.replace(/\.[^.]+$/i, "");
   const match = base.match(/(\d{8}[-_]\d+|\d{2}\.\d{2}\.\d{4}[-_]\d+|\d{2}\d{2}\d{4}[-_]\d+)/);
   return normalizeDatoLoebenrKey(match?.[1] || base);
}

export function ServitutFolderCompareModal(props: {
   rows: any[];
   onClose: () => void;
}) {
   const [folderFiles, setFolderFiles] = useState<File[]>([]);
   const [compared, setCompared] = useState(false);

   const matches = useMemo((): FolderMatchRow[] => {
      const ours = new Map<string, FolderMatchRow>();
      props.rows.forEach((row) => {
         const key = normalizeDatoLoebenrKey(getRowDatoLoebenr(row)) || row.key;
         ours.set(key, {
            key,
            label: getRowDatoLoebenr(row) || row.key,
            heading: getRowHeading(row),
            inMapit: true,
            inFolder: false,
         });
      });
      folderFiles.forEach((file) => {
         const key = parseDatoLoebenrFromFileName(file.name);
         const existing = ours.get(key);
         if (existing) {
            existing.inFolder = true;
         } else {
            ours.set(key, {
               key,
               label: file.name,
               heading: "",
               inMapit: false,
               inFolder: true,
            });
         }
      });
      return Array.from(ours.values()).sort((a, b) => a.label.localeCompare(b.label, "da"));
   }, [folderFiles, props.rows]);

   const onlyUs = matches.filter((row) => row.inMapit && !row.inFolder);
   const onlyThem = matches.filter((row) => !row.inMapit && row.inFolder);
   const both = matches.filter((row) => row.inMapit && row.inFolder);

   return (
      <div className="servitut-redegoerelse__folderOverlay" role="dialog" aria-modal="true">
         <div className="servitut-redegoerelse__folderDialog">
            <h3>{Localization.getText("Compare with folder")}</h3>
            <p>{Localization.getText("Select the PDFs you received. Matching uses date/serial no. from the file name.")}</p>
            <input
               type="file"
               multiple
               accept="application/pdf,.pdf"
               onChange={(event) => {
                  setFolderFiles(Array.from(event.currentTarget.files || []));
                  setCompared(true);
                  event.currentTarget.value = "";
               }}
            />
            {compared ? (
               <div className="servitut-redegoerelse__folderLists">
                  <section>
                     <h4>{Localization.getFormattedText("Only we have ({count})", { count: onlyUs.length })}</h4>
                     <ul>{onlyUs.map((row) => <li key={row.key}>{row.label} {row.heading}</li>)}</ul>
                  </section>
                  <section>
                     <h4>{Localization.getFormattedText("Only the folder has ({count})", { count: onlyThem.length })}</h4>
                     <ul>{onlyThem.map((row) => <li key={row.key}>{row.label}</li>)}</ul>
                  </section>
                  <section>
                     <h4>{Localization.getFormattedText("In both ({count})", { count: both.length })}</h4>
                     <p className="servitut-redegoerelse__folderNote">{Localization.getText("Cannot compare content")}</p>
                     <ul>{both.map((row) => <li key={row.key}>{row.label} {row.heading}</li>)}</ul>
                  </section>
               </div>
            ) : null}
            <div className="servitut-redegoerelse__folderActions">
               <GlassButton onClick={props.onClose}>{Localization.getText("Close")}</GlassButton>
            </div>
         </div>
      </div>
   );
}
